import { Component, Inject, isDevMode, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { CentreService } from '../../../../../services/centre.service';
import { environment } from '../../../../../../environments/environment';
import * as firebase from 'firebase/compat/app';

const Timestamp = firebase.default.firestore.Timestamp;
const FieldPath  = firebase.default.firestore.FieldPath;

export interface DocumentItem {
  url: string;
  fileName: string;
  description?: string;
  date?: any;
  dateAdded?: any;
  centreName?: string;
  sessionType?: string;
  selected?: boolean;
}

/** Individual-only session types */
const INDIVIDUAL_ONLY_TYPES = ['Rapport Building', 'Baseline Assessment', 'Intermediate Assessment', 'Discharge Assessment'];
/** Group-only session types */
const GROUP_ONLY_TYPES = ['Activity Session', 'Special Day'];

@Component({
  selector: 'app-education-photo-gallery',
  templateUrl: './education-photo-gallery.component.html',
  styleUrls: ['./education-photo-gallery.component.scss']
})
export class EducationPhotoGalleryComponent implements OnInit {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  documents: DocumentItem[] = [];
  isLoading = false;
  selectedCentre: string = '';
  startDate: Date | null = null;
  endDate: Date | null = null;
  centres$ = this.centreService.getAllCentres();

  // ── Session type filter ──────────────────────────────────────────────────
  selectedSessionType: string = 'All';
  sessionTypeOptions = [
    { label: 'Rapport Building',         value: 'Rapport Building' },
    { label: 'Baseline Assessment',      value: 'Baseline Assessment' },
    { label: 'Intermediate Assessment',  value: 'Intermediate Assessment' },
    { label: 'Discharge Assessment',     value: 'Discharge Assessment' },
    { label: 'Regular Study',            value: 'Regular Study' },
    { label: 'Activity Session (Group)', value: 'Activity Session' },
    { label: 'Special Day (Group)',      value: 'Special Day' },
  ];

  // Selection mode
  selectionMode = false;
  selectedDocuments: DocumentItem[] = [];

  // View mode
  viewMode: 'grid' | 'list' = 'grid';

  constructor(
    public dialogRef: MatDialogRef<EducationPhotoGalleryComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private afs: AngularFirestore,
    private centreService: CentreService
  ) {
    // Initialize with data passed from parent if available
    if (data) {
      this.selectedCentre = data.centreCode || '';
      this.startDate = data.startDate || null;
      this.endDate = data.endDate || null;
    }
  }

  ngOnInit(): void {
    // Auto-load if initial data provided
    if (this.selectedCentre && this.startDate && this.endDate) {
      this.loadDocuments();
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  private get shouldQueryIndividual(): boolean {
    return this.selectedSessionType === 'All' ||
      !GROUP_ONLY_TYPES.includes(this.selectedSessionType);
  }
  private get shouldQueryGroup(): boolean {
    return this.selectedSessionType === 'All' ||
      !INDIVIDUAL_ONLY_TYPES.includes(this.selectedSessionType);
  }
  private chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
    return chunks;
  }
  private pushDocs(data: any, sessionType?: string): void {
    (data.documents || []).forEach((docItem: any) => {
      if (docItem.url) {
        this.documents.push({
          url:         docItem.url,
          fileName:    docItem.fileName || this.extractFileName(docItem.url),
          description: docItem.description || '',
          date:        data.date || docItem.date || docItem.dateAdded,
          dateAdded:   docItem.dateAdded || docItem.date,
          centreName:  data.centreName || '',
          sessionType: sessionType || data.sessionType || '',
          selected:    false
        });
      }
    });
  }

  /**
   * Load documents from Firestore based on filters.
   * When sessionType is 'All': queries ALDocuments directly (existing behaviour).
   * When a specific type is selected: two-phase query via source session collection.
   */
  async loadDocuments(): Promise<void> {
    if (!this.selectedCentre || !this.startDate || !this.endDate) {
      alert('Please select centre and date range');
      return;
    }

    this.isLoading = true;
    this.documents = [];
    console.log('🔍 Fetching education documents | centre:', this.selectedCentre,
      '| type:', this.selectedSessionType);

    try {
      const startTimestamp = Timestamp.fromDate(new Date(new Date(this.startDate).setHours(0, 0, 0, 0)));
      const endTimestamp   = Timestamp.fromDate(new Date(new Date(this.endDate).setHours(23, 59, 59, 999)));

      // ── INDIVIDUAL SESSIONS ──────────────────────────────────────────────
      if (this.shouldQueryIndividual) {
        if (this.selectedSessionType === 'All') {
          // Direct ALDocuments query (unchanged)
          const snap = await this.afs
            .collection(`${this.env.documents}/Education/individualSessions`, ref =>
              ref.where('centreCode', '==', this.selectedCentre)
                 .where('date', '>=', startTimestamp)
                 .where('date', '<=', endTimestamp))
            .get().toPromise();
          snap?.docs.forEach(doc => this.pushDocs(doc.data() as any));
        } else {
          // Phase 1: find matching session IDs in educationTest
          const sessionSnap = await this.afs
            .collection(this.env.education, ref => {
              let q: any = ref
                .where('sessionDate', '>=', startTimestamp)
                .where('sessionDate', '<=', endTimestamp);
              if (this.selectedCentre !== 'All') {
                q = q.where('centreCode', '==', this.selectedCentre);
              }
              return q;
            }).get().toPromise();

          const sessionIds = (sessionSnap?.docs || [])
            .filter(d => (d.data() as any)?.sessionType?.trim() === this.selectedSessionType)
            .map(d => d.id);

          console.log(`📋 Individual session IDs for type "${this.selectedSessionType}":`, sessionIds.length);

          // Phase 2: fetch ALDocuments by those IDs (Firestore 'in' limit = 10)
          for (const chunk of this.chunkArray(sessionIds, 10)) {
            const docsSnap = await this.afs.firestore
              .collection(`${this.env.documents}/Education/individualSessions`)
              .where(FieldPath.documentId(), 'in', chunk)
              .get();
            docsSnap.docs.forEach(doc =>
              this.pushDocs(doc.data() as any, this.selectedSessionType));
          }
        }
      }

      // ── GROUP SESSIONS ───────────────────────────────────────────────────
      if (this.shouldQueryGroup) {
        if (this.selectedSessionType === 'All') {
          // Direct ALDocuments query (unchanged)
          const snap = await this.afs
            .collection(`${this.env.documents}/Education/groupSessions`, ref =>
              ref.where('date', '>=', startTimestamp)
                 .where('date', '<=', endTimestamp))
            .get().toPromise();
          snap?.docs.forEach(doc => {
            const data = doc.data() as any;
            const cc = data.centreCode || '';
            if (this.selectedCentre === 'All' || cc.includes(this.selectedCentre)) {
              this.pushDocs(data);
            }
          });
        } else {
          // Phase 1: find matching session IDs in educationGroupTest
          const sessionSnap = await this.afs
            .collection(this.env.educationGroup, ref =>
              ref.where('sessionDate', '>=', startTimestamp)
                 .where('sessionDate', '<=', endTimestamp))
            .get().toPromise();

          const sessionIds = (sessionSnap?.docs || [])
            .filter(d => {
              const data = d.data() as any;
              const typeMatch   = (data.sessionType || '').trim() === this.selectedSessionType;
              const centreMatch = this.selectedCentre === 'All' ||
                (data.centres || []).some((c: any) => c.centreCode === this.selectedCentre);
              return typeMatch && centreMatch;
            })
            .map(d => d.id);

          console.log(`📋 Group session IDs for type "${this.selectedSessionType}":`, sessionIds.length);

          // Phase 2: fetch ALDocuments by those IDs
          for (const chunk of this.chunkArray(sessionIds, 10)) {
            const docsSnap = await this.afs.firestore
              .collection(`${this.env.documents}/Education/groupSessions`)
              .where(FieldPath.documentId(), 'in', chunk)
              .get();
            docsSnap.docs.forEach(doc =>
              this.pushDocs(doc.data() as any, this.selectedSessionType));
          }
        }
      }

      console.log(`✅ Total documents loaded: ${this.documents.length}`);
      if (this.documents.length === 0) {
        alert('No documents found for the selected filters');
      }

    } catch (error) {
      console.error('❌ Error loading documents:', error);
      alert('Error loading documents: ' + (error as any).message);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Extract filename from storage URL
   */
  extractFileName(url: string): string {
    try {
      const decodedUrl = decodeURIComponent(url);
      const matches = decodedUrl.match(/\/([^\/\?]+)(\?|$)/);
      return matches ? matches[1] : 'Document';
    } catch {
      return 'Document';
    }
  }

  /**
   * Check if URL is an image
   */
  isImage(url: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext));
  }

  /**
   * Check if file is a PDF
   */
  isPdf(fileName: string): boolean {
    return fileName.toLowerCase().endsWith('.pdf');
  }

  /**
   * Select all documents
   */
  selectAll(): void {
    if (!this.selectionMode) return;

    this.documents.forEach(doc => {
      if (!doc.selected) {
        doc.selected = true;
        this.selectedDocuments.push(doc);
      }
    });
    console.log(`Selected all documents: ${this.selectedDocuments.length}`);
  }

  /**
   * Deselect all documents
   */
  deselectAll(): void {
    this.documents.forEach(doc => doc.selected = false);
    this.selectedDocuments = [];
    console.log('Deselected all documents');
  }

  /**
   * Open image in new tab
   */
  openImage(url: string): void {
    window.open(url, '_blank');
  }

  /**
   * Download image/document
   */
  downloadImage(url: string, fileName: string): void {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(error => {
        console.error('Error downloading file:', error);
        alert('Error downloading file. Please try again.');
      });
  }

  /**
   * Toggle selection mode
   */
  toggleSelectionMode(): void {
    this.selectionMode = !this.selectionMode;

    if (!this.selectionMode) {
      // Clear selections when exiting selection mode
      this.documents.forEach(doc => doc.selected = false);
      this.selectedDocuments = [];
    }
  }

  /**
   * Toggle document selection
   */
  toggleSelection(doc: DocumentItem): void {
    if (!this.selectionMode) return;

    doc.selected = !doc.selected;

    if (doc.selected) {
      this.selectedDocuments.push(doc);
    } else {
      const index = this.selectedDocuments.findIndex(d => d.url === doc.url);
      if (index > -1) {
        this.selectedDocuments.splice(index, 1);
      }
    }

    console.log(`Selected documents: ${this.selectedDocuments.length}`);
  }

  /**
   * Confirm selection and close dialog
   */
  confirmSelection(): void {
    if (this.selectedDocuments.length === 0) {
      alert('Please select at least one document');
      return;
    }

    console.log('📤 Returning selected documents:', this.selectedDocuments);
    this.dialogRef.close(this.selectedDocuments);
  }

  /**
   * Cancel selection and exit selection mode
   */
  cancelSelection(): void {
    this.selectionMode = false;
    this.documents.forEach(doc => doc.selected = false);
    this.selectedDocuments = [];
  }

  /**
   * Toggle view mode between grid and list
   */
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.selectedCentre      = '';
    this.startDate           = null;
    this.endDate             = null;
    this.selectedSessionType = 'All';
    this.documents           = [];
    this.selectedDocuments   = [];
    this.selectionMode       = false;
  }

  /**
   * Close dialog without selection
   */
  closeDialog(): void {
    this.dialogRef.close();
  }

  /**
   * Format date for display
   */
  formatDate(date: any): string {
    if (!date) return 'N/A';

    try {
      if (date.toDate) {
        return date.toDate().toLocaleDateString();
      } else if (date instanceof Date) {
        return date.toLocaleDateString();
      } else {
        return new Date(date).toLocaleDateString();
      }
    } catch {
      return 'N/A';
    }
  }
}
